import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';
import ChatInput from './ChatInput';
import ChatMessages from './ChatMessages';
import ChatThinking from './ChatThinking';
import ChatSidebar, { relativeTime } from './ChatSidebar';
import {
  createConversation,
  deleteConversation,
  getConversation,
  listConversations,
  listProjects,
  renameConversation,
  sendConversationMessage,
} from './chatApi';
import './ChatPage.css';

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const TYPEWRITER_CHUNK_SIZE = 4;
const TYPEWRITER_DELAY_MS = 14;

const PROMPTS = [
  {
    icon: 'bi-journal-richtext',
    text: 'How does the CBASS assay measure coral thermal limits? Cite the methods papers and show a relevant figure.',
  },
  {
    icon: 'bi-thermometer-half',
    text: 'Which colonies have the highest ED50, and what is their CoRTAD thermal history?',
  },
  {
    icon: 'bi-globe-americas',
    text: 'Summarize bleaching near a CoralFuture project and give me a map link.',
  },
];

const ChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { authData, loading: authLoading } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);

  const displayName = authData.firstName || authData.username || 'there';
  const isLanding = !conversationId && messages.length === 0;

  const refreshConversations = async () => {
    const items = await listConversations();
    setConversations(items);
  };

  useEffect(() => {
    if (!authData.authenticated) return undefined;
    refreshConversations().catch((err) => setError(err.message));
    listProjects().then(setProjects).catch(() => setProjects([]));
    return undefined;
  }, [authData.authenticated]);

  useEffect(() => {
    if (!conversationId || !authData.authenticated) {
      if (!conversationId) setMessages([]);
      return undefined;
    }
    let cancelled = false;
    getConversation(conversationId)
      .then((data) => {
        if (cancelled) return;
        setMessages(data.messages || []);
        setProjectId(data.projectId ? String(data.projectId) : '');
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          navigate('/chat', { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, authData.authenticated, navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, typing]);

  useEffect(() => () => {
    if (typingTimerRef.current) window.clearInterval(typingTimerRef.current);
  }, []);

  if (authLoading) return null;
  if (!authData.authenticated) return <Navigate to="/login" replace />;

  const typeAssistantMessage = (content, links, attachments = []) => {
    const id = makeId();
    let index = 0;
    setTyping(true);
    setMessages((current) => [
      ...current,
      { id, role: 'assistant', content: '', links, attachments, streaming: true },
    ]);
    typingTimerRef.current = window.setInterval(() => {
      index = Math.min(index + TYPEWRITER_CHUNK_SIZE, content.length);
      setMessages((current) => current.map((message) => (
        message.id === id
          ? { ...message, content: content.slice(0, index), streaming: index < content.length }
          : message
      )));
      if (index >= content.length) {
        window.clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        setTyping(false);
      }
    }, TYPEWRITER_DELAY_MS);
  };

  const localFiles = (files) => files.map((file, index) => ({
    id: `local-${index}-${file.name}`,
    originalName: file.name,
    url: URL.createObjectURL(file),
    isImage: file.type.startsWith('image/'),
  }));

  const handleSend = async (text, { files = [], projectId: selectedProject } = {}) => {
    const userMessage = {
      id: makeId(),
      role: 'user',
      content: text,
      attachments: localFiles(files),
    };
    setMessages((current) => [...current, userMessage]);
    setLoading(true);
    setError(null);
    try {
      if (!conversationId) {
        const created = await createConversation({
          content: text,
          files,
          projectId: selectedProject || projectId,
        });
        await refreshConversations();
        navigate(`/chat/${created.id}`, { replace: true });
        setMessages(created.messages || []);
        return;
      }
      const data = await sendConversationMessage(conversationId, { content: text, files });
      if (data.userMessage) {
        setMessages((current) => current.map((message) => (
          message.id === userMessage.id ? { ...data.userMessage } : message
        )));
      }
      typeAssistantMessage(
        data.message || data.assistantMessage?.content || 'I could not find an answer.',
        data.links || data.assistantMessage?.links || [],
      );
      await refreshConversations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteConversation(id);
    await refreshConversations();
    if (String(id) === String(conversationId)) {
      setMessages([]);
      navigate('/chat');
    }
  };

  const handleRename = async (id, title) => {
    await renameConversation(id, title);
    await refreshConversations();
  };

  return (
    <div className="chat-page">
      <ChatSidebar
        conversations={conversations}
        activeId={conversationId}
        onNew={() => {
          setMessages([]);
          setError(null);
          navigate('/chat');
        }}
        onSelect={(id) => navigate(`/chat/${id}`)}
        onRename={handleRename}
        onDelete={handleDelete}
      />
      <section className="chat-shell">
        <main className="chat-main">
          {isLanding && (
            <div className="chat-empty">
              <img src="/chat-logo.png" alt="" className="chat-mark" />
              <h1>Hello, {displayName}. What can I help you explore?</h1>
              <p>Ask about CBASS methods, CoralFuture data, CoRTAD, bleaching maps, or drop a file into the chat.</p>
            </div>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          <ChatMessages messages={messages} />
          {loading && <ChatThinking mode="search" />}
          {typing && !loading && <ChatThinking mode="write" />}
          <div ref={bottomRef} />
        </main>
        <footer className="chat-composer">
          <ChatInput
            disabled={loading || typing}
            onSend={handleSend}
            projects={projects}
            projectId={projectId}
            onProjectChange={setProjectId}
          />
          {isLanding && (
            <>
              <div className="chat-prompt-grid">
                {PROMPTS.map((prompt) => (
                  <button
                    key={prompt.text}
                    type="button"
                    className="chat-prompt-card"
                    disabled={loading || typing}
                    onClick={() => handleSend(prompt.text, { files: [], projectId })}
                  >
                    <i className={`bi ${prompt.icon}`} />
                    <span>{prompt.text}</span>
                  </button>
                ))}
              </div>
              {conversations.length > 0 && (
                <div className="chat-recent">
                  <h2>Recent chats</h2>
                  {conversations.slice(0, 6).map((conversation) => (
                    <button
                      key={conversation.id}
                      type="button"
                      className="chat-recent-item"
                      onClick={() => navigate(`/chat/${conversation.id}`)}
                    >
                      <span>{conversation.title}</span>
                      <time>{relativeTime(conversation.updatedAt)}</time>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </footer>
      </section>
    </div>
  );
};

export default ChatPage;
